import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { customerService } from '../../services/customerService';
import { getCurrentUser } from '../../services/authService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';

/**
 * Shared form component for adding/editing customers
 * @param {Object} props Component props
 * @param {Function} props.onSuccess Callback when customer is created/updated successfully
 * @param {Function} props.onCancel Callback when form is cancelled
 * @param {Object} props.initialData Initial form data (for editing)
 * @param {React.RefObject} props.nameInputRef Optional ref for the Name field (dialog focus)
 * @returns {JSX.Element} Customer form component
 */
const CustomerForm = ({ onSuccess, onCancel, initialData = null, nameInputRef: nameInputRefProp }) => {
  const user = getCurrentUser();
  const internalNameRef = useRef(null);
  const nameInputRef = nameInputRefProp || internalNameRef;

  // Form state
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    contactPerson: initialData?.contactPerson || '',
    address: initialData?.address || '',
    website: initialData?.website || '',
    notes: initialData?.notes || '',
    gst: initialData?.gst || '',
    stateCode: initialData?.stateCode || '',
    openingBalance: initialData?.openingBalance || 0,
    companyId: initialData?.companyId || user?.companyId || 1
  });

  const [gstStates, setGstStates] = useState([]);

  // Validation state
  const [errors, setErrors] = useState({});
  const [checkingPhone, setCheckingPhone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_CONFIG.ENDPOINTS.STATES);
        if (!cancelled && Array.isArray(res.data)) {
          setGstStates(res.data);
        }
      } catch (e) {
        console.error('Failed to load GST states', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  const isValidEmail = (email) => {
    if (!email) return true;
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return pattern.test(email);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Name is required
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    // Phone is required
    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = 'Phone is required';
    }
    // Phone length limit
    if (formData.phone && formData.phone.replace(/\D/g, '').length > 15) {
      newErrors.phone = 'Phone must be at most 15 digits';
    }
    // Email format validation
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    // Company-wise duplicate phone check
    try {
      setCheckingPhone(true);
      const existing = await customerService.getCustomersByCompany(formData.companyId);
      const digits = (formData.phone || '').replace(/\D/g, '');
      const conflict = (existing || []).some(c => (c.phone || '').replace(/\D/g, '') === digits && c.id !== initialData?.id);
      if (conflict) {
        setErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
        setCheckingPhone(false);
        return;
      }
      setCheckingPhone(false);
    } catch (dupErr) {
      setCheckingPhone(false);
    }
    
    try {
      // Parse the opening balance as a float with 2 decimal places
      const initialBalance = parseFloat(parseFloat(formData.openingBalance || 0).toFixed(2));
      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        contactPerson: formData.contactPerson,
        address: formData.address,
        website: formData.website,
        notes: formData.notes,
        gst: formData.gst,
        stateCode: formData.stateCode || null,
        openingBalance: initialBalance,  // Set opening balance
        creditBalance: initialBalance,  // Set credit balance same as opening balance
        companyId: formData.companyId
      };
      
      let response;
      if (initialData && initialData.id) {
        response = await customerService.updateCustomer(initialData.id, customerData);
      } else {
        response = await customerService.createCustomer(customerData);
      }
      onSuccess(response);
    } catch (error) {
      console.error('Error saving customer:', error);
      // Let parent component handle error display
      onSuccess(null, error.response?.data?.message || 'Failed to save customer');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') {
      v = value.replace(/\D/g, '').slice(0, 15);
    }
    setFormData(prev => ({
      ...prev,
      [name]: v
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
    
    // Trigger duplicate check when phone changes and has reasonable length
    if (name === 'phone' && v && v.length >= 7) {
      (async () => {
        try {
          const existing = await customerService.getCustomersByCompany(formData.companyId);
          const conflict = (existing || []).some(c => (c.phone || '').replace(/\D/g, '') === v && c.id !== initialData?.id);
          if (conflict) {
            setErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
          }
        } catch (err) {}
      })();
    }
  };

  return (
    <>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            inputRef={nameInputRef}
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
            size="small"
            autoComplete="off"
            inputProps={{ maxLength: 100 }}
          />
          
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            error={!!errors.email}
            helperText={errors.email}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
            error={!!errors.phone}
            helperText={errors.phone}
            margin="normal"
            inputProps={{ maxLength: 15 }}
          />
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={2}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="GST Number"
            name="gst"
            value={formData.gst}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ maxLength: 50 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="customer-state-label">State (place of supply)</InputLabel>
            <Select
              labelId="customer-state-label"
              value={formData.stateCode || ''}
              label="State (place of supply)"
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, stateCode: e.target.value }));
              }}
            >
              <MenuItem value="">
                <em>Not selected</em>
              </MenuItem>
              {gstStates.map((s) => (
                <MenuItem key={s.stateCode} value={s.stateCode}>
                  {s.stateCode} — {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Opening Balance"
            name="openingBalance"
            type="number"
            value={formData.openingBalance}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ step: "0.01", min: "0" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {initialData ? 'Save Changes' : 'Add Customer'}
        </Button>
      </DialogActions>
    </>
  );
};

export default CustomerForm; 
